import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/shared/contexts/AuthContext';
import { supportApi } from '../services/supportApi';

/**
 * useSupportUnread — sinal de "tem ticket com resposta nova".
 *
 * Como detecta:
 *   1. faz polling (60s) em `/support/tickets` filtrado pelo escopo do
 *      backend (CUSTOMER/CS_LEAD/ADMIN — o BFF e o janus-support tratam
 *      isso, ver `_ticket_role_for`);
 *   2. compara o `updated_at` de cada ticket com o `last_seen_at`
 *      armazenado no localStorage por (tenantId, ticketId);
 *   3. um ticket conta como "unread" se foi atualizado depois da
 *      última vez que o usuário entrou na inbox OU se nunca foi visto
 *      e está em estado `open|in_progress|reopened|waiting_customer`.
 *
 * Cleared quando: o usuário entra na tela `/admin/support/tickets`
 * (chamando `markAllSeen()` no mount do SupportInboxPage). A política
 * "para de piscar ao entrar na tela" foi escolhida pelo produto em
 * vez de granular por ticket pra simplificar o UX.
 *
 * NOTA: o sinal vive no localStorage (não sincroniza entre dispositivos).
 * Trade-off intencional pra não criar endpoint novo no backend nesta fase.
 */

const STORAGE_KEY_PREFIX = 'support_last_seen_at_v1';
const POLL_INTERVAL_MS = 60_000;

function storageKey(tenantId: string | undefined): string {
  return `${STORAGE_KEY_PREFIX}:${tenantId ?? 'anon'}`;
}

function readLastSeenAt(tenantId: string | undefined): number {
  try {
    const raw = window.localStorage.getItem(storageKey(tenantId));
    if (!raw) return 0;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

function writeLastSeenAt(tenantId: string | undefined, ts: number): void {
  try {
    window.localStorage.setItem(storageKey(tenantId), String(ts));
  } catch {
    /* localStorage cheio / disabled — sem fallback, o badge volta sumindo
     * naturalmente quando a query expira ou o tab fecha. */
  }
}

interface UseSupportUnreadResult {
  /** Total de tickets com updated_at > last_seen_at. */
  unreadCount: number;
  /** Set de IDs com indicador "nova resposta" na linha. */
  unreadIds: Set<string>;
  /** Chamar ao entrar na inbox — limpa o badge global da sidebar. */
  markAllSeen: () => void;
}

export function useSupportUnread(): UseSupportUnreadResult {
  const { user, isAuthenticated } = useAuth();
  const tenantId = user?.tenantId;

  // Mantemos uma cópia do last_seen_at em state pra forçar re-render
  // quando o usuário marcar como visto. localStorage por si só não
  // dispara updates.
  const [lastSeenAt, setLastSeenAt] = useState<number>(() => readLastSeenAt(tenantId));

  // Re-read quando tenant muda (impersonation, switch de conta).
  useEffect(() => {
    setLastSeenAt(readLastSeenAt(tenantId));
  }, [tenantId]);

  const { data } = useQuery({
    queryKey: ['support', 'unread-poll', tenantId],
    queryFn: async () => {
      // Limit 50: alinhado com /tickets/mine. Pega os mais recentes
      // (sort by updated_at desc é o default do backend).
      return supportApi.listTickets({ page: 1, page_size: 50, sort_by: 'updated_at', sort_order: 'desc' });
    },
    enabled: isAuthenticated && !!tenantId,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    // Não bloqueia render: erros silenciam (badge fica 0).
    retry: false,
    staleTime: 30_000,
  });

  const unreadIds = new Set<string>();
  if (data?.items) {
    for (const t of data.items) {
      const updatedMs = new Date(t.updated_at).getTime();
      if (Number.isFinite(updatedMs) && updatedMs > lastSeenAt) {
        unreadIds.add(t.id);
      }
    }
  }

  const markAllSeen = useCallback(() => {
    const now = Date.now();
    writeLastSeenAt(tenantId, now);
    setLastSeenAt(now);
  }, [tenantId]);

  return {
    unreadCount: unreadIds.size,
    unreadIds,
    markAllSeen,
  };
}
